import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/utils';
import { Building2, PlusCircle, MapPin, Bed, Bath, Eye, MoreVertical, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

export default function Properties() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [listings, setListings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const { data } = await api.get('/properties');
      setProperties(data);
      
      // Load listings for each property
      const listingsData = {};
      try {
        const { data: allListings } = await api.get('/listings');
        for (const listing of allListings) {
          if (!listingsData[listing.property_id]) {
            listingsData[listing.property_id] = [];
          }
          listingsData[listing.property_id].push(listing);
        }
      } catch (e) {
        console.log('No listings found');
      }
      setListings(listingsData);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const getPropertyStatus = (property, propertyListings) => {
    const activeListing = propertyListings?.find(l => l.status === 'active');
    
    // Simple status logic: active if has active listing, otherwise draft
    if (activeListing) {
      return { label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle };
    } else {
      return { label: 'Draft', color: 'bg-slate-100 text-slate-600', icon: Clock };
    }
  };

  return (
    <DashboardLayout>
      {/* Mobile-first container */}
      <div className="px-0 sm:px-0 pb-20 lg:pb-6">
        {/* Header - mobile responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900">My Properties</h1>
            <p className="text-sm sm:text-base text-slate-600">Manage your property portfolio</p>
          </div>
          <Button 
            onClick={() => navigate('/landlord/properties/new')} 
            className="w-full sm:w-auto"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Add Property
          </Button>
        </div>

        {/* Properties List - mobile first */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : properties.length === 0 ? (
          <Card className="p-8 sm:p-12 text-center">
            <Building2 className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg sm:text-xl font-semibold mb-2">No properties yet</h3>
            <p className="text-slate-600 mb-6 text-sm sm:text-base">Add your first property to get started</p>
            <Button onClick={() => navigate('/landlord/properties/new')}>
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Property
            </Button>
          </Card>
        ) : (
          <div className="space-y-4 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 sm:space-y-0">
            {properties.map((property) => {
              const propertyListings = listings[property.id] || [];
              const activeListing = propertyListings.find(l => l.status === 'active');
              const status = getPropertyStatus(property, propertyListings);
              const StatusIcon = status.icon;
              
              return (
                <Card 
                  key={property.id} 
                  className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/landlord/properties/${property.id}`)}
                >
                  {/* Property Image/Gradient - smaller on mobile */}
                  <div className="h-32 sm:h-40 bg-gradient-to-br from-primary/80 to-primary relative">
                    {/* Status Badge */}
                    <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {status.label}
                    </div>
                    
                    {/* Rent overlay if active */}
                    {activeListing && (
                      <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur px-3 py-1.5 rounded-lg">
                        <span className="font-bold text-primary">{formatCurrency(activeListing.rent_amount)}</span>
                        <span className="text-slate-500 text-sm">/mo</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Property Details */}
                  <div className="p-4">
                    <h3 className="font-semibold text-base sm:text-lg truncate mb-1">
                      {property.address?.line1 || 'Address not set'}
                    </h3>
                    <p className="text-sm text-slate-500 flex items-center gap-1 mb-3">
                      <MapPin className="w-3.5 h-3.5" />
                      <span className="truncate">
                        {property.address?.city || 'City'}, {property.address?.postcode || 'Postcode'}
                      </span>
                    </p>
                    
                    {/* Property Info */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Bed className="w-4 h-4" /> {property.bedrooms || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Bath className="w-4 h-4" /> {property.bathrooms || 0}
                        </span>
                        <span className="capitalize text-xs px-2 py-0.5 bg-slate-100 rounded">
                          {property.property_type || 'flat'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-4 pt-3 border-t">
                      {!activeListing ? (
                        <Button 
                          size="sm" 
                          className="flex-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/landlord/properties/${property.id}/edit`);
                          }}
                        >
                          Complete Listing
                        </Button>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/landlord/properties/${property.id}`);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Manage
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
